import { useState } from "react";
import { Loader2, Send, Compass, Route, PenTool, Plane } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { API } from "../lib/api";
import { IMG } from "../data/packages";

const STEPS = [
  { icon: Compass, title: "Tell Us Your Vision", desc: "Share the places pulling at you, your pace, your people and your budget." },
  { icon: Route, title: "Meet Your Curator", desc: "A dedicated route architect drafts the skeleton of your journey within 48 hours." },
  { icon: PenTool, title: "Refine Together", desc: "We iterate — stays, experiences, meals — until the itinerary feels like it was always yours." },
  { icon: Plane, title: "Seamless Voyage", desc: "Documents, transfers and a 24/7 helpline. You just show up and belong." },
];

const STYLES = ["Adventure", "Luxury", "Culture & Heritage", "Wellness", "Wildlife", "Road-trip", "Food & Cafés", "Photography"];
const BUDGETS = ["Under ₹25,000", "₹25,000 – ₹75,000", "₹75,000 – ₹1,50,000", "₹1,50,000+", "Flexible"];

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-aurora/60 focus:ring-2 focus:ring-aurora/20";

export default function CustomisedTrips() {
  const [duration, setDuration] = useState(8);
  const [styles, setStyles] = useState([]);
  const [budget, setBudget] = useState(BUDGETS[1]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleStyle = (s) => setStyles((arr) => (arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        trip_type: "Customised Trip",
        destination: form.destination || null,
        travel_date: null,
        group_size: null,
        budget,
        message: [form.message, `Duration: ~${duration} days`, `Styles: ${styles.join(", ") || "open to suggestions"}`].filter(Boolean).join(" · "),
        source: "customised-trips-page",
      });
      toast.success("Your custom journey brief is with our curators. Expect a draft route within 48 hours.");
      setForm({ name: "", email: "", phone: "", destination: "", message: "" });
      setStyles([]);
      setDuration(8);
    } catch (err) {
      toast.error("Could not submit your brief. Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-customised-trips">
      <PageHeader
        testid="customised-header"
        eyebrow="Customised Trips"
        title="Bespoke journeys,"
        accent="crafted for you"
        description="Honeymoon in the Dolomites? A retirement caravan through Rajasthan? If you can dream it, we can route it."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="process-steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="group h-full rounded-3xl border border-white/10 bg-obsidian p-7 transition-all duration-500 hover:border-aurora/40 hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <s.icon className="h-6 w-6 text-aurora" />
                  <span className="font-mono text-xs tracking-[0.2em] text-comet">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-6 font-display text-lg font-bold text-slate-50">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid items-start gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
              <img src={IMG.stargazing} alt="Stargazing on a customised trip" loading="lazy" className="h-80 w-full object-cover lg:h-[30rem]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
              <div className="absolute inset-x-7 bottom-7">
                <p className="font-editorial text-2xl italic leading-snug text-slate-100">
                  "Tell us your dates and your dreams — we will draw the map between them."
                </p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">The Cosmos Routes Promise</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="customiser-form" className="rounded-[2rem] border border-white/10 bg-obsidian/70 p-7 md:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Trip Customiser</p>
              <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-slate-50 md:text-3xl">
                Design your <em className="font-editorial font-medium italic text-comet">own route</em>
              </h2>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <input required minLength={2} data-testid="customiser-name-input" className={fieldCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid="customiser-email-input" className={fieldCls} placeholder="Email *" value={form.email} onChange={set("email")} />
              </div>
              <input required type="tel" minLength={6} data-testid="customiser-phone-input" className={`${fieldCls} mt-5`} placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} />
              <input data-testid="customiser-destination-input" className={`${fieldCls} mt-5`} placeholder="Where in the world? (destination or mood)" value={form.destination} onChange={set("destination")} />

              <div className="mt-7">
                <div className="flex items-center justify-between">
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Trip Duration</label>
                  <span data-testid="customiser-duration-output" className="font-display text-sm font-bold text-aurora">
                    ≈ {duration} days
                  </span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="21"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  data-testid="customiser-duration-slider"
                  className="mt-3 w-full cursor-pointer accent-[#00F5D4]"
                />
                <div className="mt-1 flex justify-between font-mono text-[9px] text-slate-600">
                  <span>3 DAYS</span>
                  <span>21 DAYS</span>
                </div>
              </div>

              <div className="mt-7">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Travel Styles</label>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="customiser-style-chips">
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`style-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => toggleStyle(s)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                        styles.includes(s)
                          ? "border-aurora bg-aurora/15 text-aurora"
                          : "border-white/15 text-slate-400 hover:border-aurora/40 hover:text-aurora"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Budget Per Person</label>
                <select data-testid="customiser-budget-select" className={`${fieldCls} mt-3 [&>option]:bg-obsidian`} value={budget} onChange={(e) => setBudget(e.target.value)}>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <textarea rows={3} data-testid="customiser-message-input" className={`${fieldCls} mt-5 resize-none`} placeholder="Must-sees, celebrations, pace — anything at all…" value={form.message} onChange={set("message")} />

              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={busy}
                data-testid="customiser-submit-btn"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-aurora py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,245,212,0.4)] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {busy ? "Crafting…" : "Request My Custom Route"}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
